import { useState } from 'react';
import { ConfirmModal } from '@safetyhub/ui';
import type { TemplatePolicy } from '@safetyhub/shared';
import { SELECT_CLS } from './editorFieldClasses';
import {
  SPLIT_AXIS_OPTIONS,
  applySplitAxis,
  describeSplitDetail,
  getSplitAxisProvider,
  hasSplitDetail,
} from './splitAxisPolicy';

/**
 * 템플릿 전역 속성 — 「분할 축」 구획(2026-09-02 P5-b).
 *
 * 저작이 고르는 것은 축 하나뿐이고(`policy.split.provider`), 나눌지 말지는 작성자가 진입
 * 화면에서 고른다. 축 판정·전환 규칙은 `./splitAxisPolicy`가 소유한다 — 여기는 그리기만.
 *
 * 「없음」으로 되돌릴 때만 확인을 받는다. 이 화면엔 세부 선언(행 표시명·그룹명 주입 칸 등)의
 * 편집 칸이 없어서(코드·seed가 소유), 축을 지우면 그 선언들을 되살릴 방법이 저작자에게 없다.
 */
export function SplitAxisSection({
  policy,
  onChange,
}: {
  policy: TemplatePolicy | undefined;
  onChange: (next: TemplatePolicy) => void;
}) {
  const [confirmingClear, setConfirmingClear] = useState(false);
  const current = getSplitAxisProvider(policy);
  const losing = describeSplitDetail(policy);

  const select = (provider: string): void => {
    // 세부가 남아 있는 축을 끄는 것만 되돌릴 수 없다 — 나머지 전환은 즉시 반영.
    if (!provider && hasSplitDetail(policy)) {
      setConfirmingClear(true);
      return;
    }
    onChange(applySplitAxis(policy, provider));
  };

  return (
    <div className="pt-2 border-t border-gray-100">
      <span className="text-xs font-medium text-gray-600">분할 축</span>
      <p className="mt-1 text-[11px] text-gray-400">
        작성 페이지에 들어갈 때 점검 데이터를 무엇으로 묶어 나눌지 정합니다. 축을 고르면 작성자가
        진입 시 그룹을 확인하고 나눠 만들지 한 건으로 만들지 고릅니다. 「없음」이면 항상 한
        건입니다.
      </p>
      <label className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-600">
        <span className="shrink-0">묶는 기준</span>
        {/* 접근명은 감싼 label의 텍스트("묶는 기준")다 — aria-label로 덮으면 보이는 라벨과
            읽히는 이름이 갈린다(WCAG 2.5.3). 구획 제목이 「분할 축」 맥락을 준다. */}
        <select
          value={current}
          onChange={(e) => select(e.target.value)}
          className={`${SELECT_CLS} max-w-[10rem]`}
        >
          {SPLIT_AXIS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <ConfirmModal
        open={confirmingClear}
        danger
        title="분할 축을 해제할까요?"
        confirmLabel="해제"
        body={
          <div className="space-y-2 text-sm text-gray-600">
            <p>이 양식은 앞으로 항상 한 건으로 만들어집니다.</p>
            <p>
              함께 지워지는 선언: <span className="text-gray-900">{losing.join(', ')}</span>. 이
              화면에는 다시 넣을 칸이 없어 되돌리려면 양식 정의를 고쳐야 합니다.
            </p>
          </div>
        }
        onConfirm={() => {
          onChange(applySplitAxis(policy, ''));
          setConfirmingClear(false);
        }}
        onClose={() => setConfirmingClear(false)}
      />
    </div>
  );
}
